/**
 * The HRT / TRT Plan tab, drawn from "HRT-TRT Patient View - screenshot.png":
 *
 *   1 Choose a Hormone Pathway: the engine's pathways in its own ranking order, each with its Treatment Relevance
 *     band (a word, never a percent), the Schedule III chip, "Why?" (the WHY card and its evidence), the review and
 *     caution notes, then the products from the pharmacy list with the dose as the pharmacy lists it, a price box
 *     and the OPR price; the dark box with what the patient pays; "Not relevant now (n)"; locked pathways last,
 *     each with what locks it and what unlocks it.
 *   2 Safety and Readiness, 3 Clinical Twin Outcome Prediction, 4 Monitoring After Start.
 *   5 once the pathway is saved: the plan for the chosen products, through the same release, sign-off checks and
 *     Review and sign drawer as every other plan (HrtSendStep).
 *
 * The pathway choice goes through POST /hrt/patients/:id/selection, which runs the engine's validateSelection():
 * a LOCKED pathway, a missing reason or a medicine from another pathway is refused there, and a stale evaluation
 * returns 409, so the screen reloads and asks again. MCOS never auto-prescribes: the ticks are the clinician's.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { IconCheck } from './icons';
import { InfoHint, LoadingRows } from './ui';
import { PriceBox, useOprPrices, type OprItem } from './OprPricing';
import { pricingBanner } from '@mcos/types';
import { bannerLines, usd, wholeUsd } from './OprPricing';
import { BandBox, EmptyCard, Fold, HintButton, HrtChip, LockGlyph, RadioMark, SaveNote, StepCard, TickBox, WhyCard, fieldClass } from './HrtUi';
import { MonitoringStep, PendingDecisionsBanner, ReadinessStep, TwinStep } from './HrtSections';
import { HrtSendStep } from './HrtSend';
import {
  BAND_WORD,
  HRT_PROGRAM_KEY,
  defaultProducts,
  dueWindow,
  errorText,
  hrtKey,
  joinNames,
  libraryDoseLine,
  pathwaySchedule,
  productNote,
  ruleSource,
  shortDay,
  unlockWords,
  type HrtFinding,
  type HrtPathway,
  type HrtPatientView,
  type HrtProduct,
} from '../lib/hrt';

/** What the clinician has picked on this tab: kept by the panel, so the OPR Pricing tab prices the same products. */
export interface HrtPick {
  pathwayId: string | null;
  products: string[];
  reason: string;
  dose: string;
}

export type HrtTabKey = 'plan' | 'pathway' | 'labs' | 'twin' | 'checkins' | 'history' | 'notes' | 'opr' | 'mcos';

type Prices = ReturnType<typeof useOprPrices>;

/** The pick a fresh tab opens with: the saved choice when there is one, else the engine's first pathway. */
export function initialPick(view: HrtPatientView): HrtPick {
  const pathways = view.evaluation?.pathways ?? [];
  const latest = view.selections[0];
  const saved = latest?.kind === 'selected' ? pathways.find((p) => p.id === latest.pathwayId && p.band !== 'LOCKED') : undefined;
  const top = pathways.find((p) => p.section === 'consider');
  const pathway = saved ?? top ?? null;
  if (!pathway) return { pathwayId: null, products: [], reason: '', dose: '' };
  const products = view.products[pathway.id] ?? [];
  // The products the choice was saved with, when they still belong to the pathway; else this patient's defaults.
  const savedMeds = saved && latest ? latest.products.map((p) => p.medicationId).filter((m) => products.some((p) => p.medicationId === m)) : [];
  return {
    pathwayId: pathway.id,
    products: savedMeds.length > 0 ? savedMeds : defaultProducts(products),
    reason: '',
    dose: saved && latest?.doseMgWeek != null ? String(latest.doseMgWeek) : '',
  };
}

export function HrtPlanTab({
  view,
  pick,
  setPick,
  onOpenOpr,
  onOpenTab,
  onPlanSent,
}: {
  view: HrtPatientView;
  pick: HrtPick | null;
  setPick: (next: HrtPick) => void;
  onOpenOpr: () => void;
  onOpenTab: (tab: HrtTabKey) => void;
  onPlanSent?: (outcome: { tone: 'ok' | 'warn'; text: string }) => void;
}) {
  const prices = useOprPrices();
  const ev = view.evaluation;
  // A patient already on hormones opens on the current therapy; the pathway list is one click away for a change.
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    if (pick === null && ev) setPick(initialPick(view));
  }, [pick, ev, view, setPick]);

  if (!ev) {
    return (
      <>
        <PendingDecisionsBanner decisions={view.pendingDecisions} />
        <EmptyCard
          title="No hormone evaluation yet"
          body={`MCOS ranks the pathways once ${view.patient.firstName}'s intake has the hormone questions. Labs and blood pressure can be entered on Labs & Monitoring in the meantime.`}
        />
      </>
    );
  }

  if (ev.track === 'HRT-EXCEPTION' || ev.track === 'UNKNOWN') {
    return (
      <>
        <PendingDecisionsBanner decisions={view.pendingDecisions} />
        <StepCard
          step={1}
          title="Clinician review"
          subtitle={
            ev.track === 'HRT-EXCEPTION'
              ? 'This patient is on the exception track: MCOS does not rank hormone pathways for it. The treating clinician decides.'
              : "MCOS needs the patient's sex at birth from the intake before it can rank hormone pathways."
          }
        >
          {ev.exception && <p className="rounded-lg bg-paper-deep px-3.5 py-2.5 text-[13px] text-navy">{ev.exception}</p>}
        </StepCard>
      </>
    );
  }

  const current = pick ?? initialPick(view);
  const pathways = ev.pathways;
  const consider = pathways.filter((p) => p.section === 'consider');
  const notRelevant = pathways.filter((p) => p.section === 'not_relevant');
  const locked = pathways.filter((p) => p.section === 'locked');
  const picked = pathways.find((p) => p.id === current.pathwayId) ?? null;

  const choose = (pathway: HrtPathway) => {
    if (pathway.id === current.pathwayId) return;
    setPick({ pathwayId: pathway.id, products: defaultProducts(view.products[pathway.id]), reason: current.reason, dose: '' });
  };

  const latest = view.selections[0] ?? null;
  const savedHere = latest?.kind === 'selected' && picked && picked.band !== 'LOCKED' && latest.pathwayId === picked.id ? latest : null;
  // A newer lab, answer or check-in can lock the pathway that was chosen: say so above the list.
  const savedNowLocked = latest?.kind === 'selected' ? pathways.find((p) => p.id === latest.pathwayId && p.band === 'LOCKED') ?? null : null;
  const started = view.status.programme === 'started';
  const currentPathway = started && latest?.kind === 'selected' ? pathways.find((p) => p.id === latest.pathwayId) ?? null : null;
  const showCurrent = Boolean(currentPathway) && !changing;

  /* The dark box follows the card that is picked, as drawn: what the patient pays for exactly those products. */
  const cardWithSummary = (pathway: HrtPathway, quiet: boolean) => {
    const selected = pathway.id === current.pathwayId;
    return (
      <Fragment key={pathway.id}>
        <PathwayCard
          view={view}
          pathway={pathway}
          selected={selected}
          onSelect={() => choose(pathway)}
          pick={current}
          setPick={setPick}
          prices={prices}
          quiet={quiet}
        />
        {selected && (
          <li className="list-none">
            <SummaryBox pathway={pathway} view={view} pick={current} prices={prices} onOpenOpr={onOpenOpr} />
            {prices.saveError && <SaveNote tone="error">{prices.saveError}</SaveNote>}
          </li>
        )}
      </Fragment>
    );
  };

  return (
    <div>
      <PendingDecisionsBanner decisions={view.pendingDecisions} />
      <div className="space-y-2.5">
        {showCurrent && currentPathway && latest?.kind === 'selected' ? (
          <CurrentTherapy view={view} pathway={currentPathway} onChange={() => setChanging(true)} onOpenTab={onOpenTab} />
        ) : (
        <StepCard
          step={1}
          title="Choose a Hormone Pathway"
          subtitle={
            currentPathway
              ? `${view.patient.firstName} is on ${currentPathway.name.toLowerCase()} since ${shortDay(view.status.startedOn)}. A new choice is saved with its reason; the plan is sent the usual way.`
              : `MCOS ran the Clinical Pathway on ${view.patient.firstName}'s intake and labs and took off what doesn't apply. Pick one pathway, then the products from your pharmacy list. You make the final decision.`
          }
          action={
            currentPathway ? (
              <button type="button" onClick={() => setChanging(false)} className="inline-flex h-[30px] items-center rounded-md border border-hairline bg-white px-3 text-[12.5px] text-navy hover:bg-mint" data-testid="back-to-current">
                Back to the current therapy
              </button>
            ) : (
            <HintButton label="How the ranking works" testId="ranking-hint">
              <span className="block">
                Each pathway gets a Treatment Relevance band, High, Moderate or Low, from the patient's symptoms, labs and data, phenotype, route and
                any earlier response to it. A cap can lower the band; a safety lock overrides it.
              </span>
              <span className="mt-1.5 block">
                Pathways are listed by band, highest first. Pathways with no compatible symptoms are under Not relevant now; locked pathways come last
                with the reason and what unlocks them.
              </span>
              <span className="mt-1.5 block">{ev.notice}</span>
            </HintButton>
            )
          }
          testId="hrt-step-1"
        >
          {savedNowLocked && latest && (
            <p className="mb-2 rounded-lg bg-risk-yellow-soft px-3.5 py-2.5 text-[13px] text-risk-yellow" role="status" data-testid="saved-now-locked">
              <b className="font-semibold">{savedNowLocked.name}</b>, chosen {shortDay(latest.createdAt)}, is now locked:{' '}
              {savedNowLocked.locks.map((lock) => lock.text).join(' ')}{' '}
              {savedNowLocked.locks.some((lock) => lock.unlock) ? 'See Locked below for what unlocks it.' : 'See Locked below.'}
            </p>
          )}
          {latest?.kind === 'no_pathway' && (
            <p className="mb-2 rounded-lg bg-paper-deep px-3.5 py-2.5 text-[13px] text-navy" data-testid="no-pathway-record">
              <b className="font-semibold">No pathway recorded</b>
              {latest.clinician ? ` by ${latest.clinician.name}` : ''}, {shortDay(latest.createdAt)}: {latest.reason}
            </p>
          )}

          {consider.length === 0 ? (
            <p className="rounded-lg border border-dashed border-hairline px-4 py-5 text-[13.5px] text-ink-muted">
              No pathway fits {view.patient.firstName}'s answers right now. The ones below are not relevant now or locked, each with the reason.
            </p>
          ) : (
            <ul role="radiogroup" aria-label="Hormone pathways" className="mcos-stagger space-y-2">
              {consider.map((pathway) => cardWithSummary(pathway, false))}
            </ul>
          )}

          {notRelevant.length > 0 && (
            <Fold title="Not relevant now" count={notRelevant.length} defaultOpen={notRelevant.some((p) => p.id === current.pathwayId)} testId="fold-not-relevant">
              <ul role="radiogroup" aria-label="Pathways not relevant now" className="space-y-2">
                {notRelevant.map((pathway) => cardWithSummary(pathway, true))}
              </ul>
            </Fold>
          )}

          {locked.length > 0 && (
            <Fold title="Locked" count={locked.length} defaultOpen={Boolean(savedNowLocked)} icon={<LockGlyph size={15} className="text-ink-muted" />} testId="fold-locked">
              <ul className="space-y-2">
                {locked.map((pathway) => (
                  <LockedCard key={pathway.id} view={view} pathway={pathway} onOpenTab={onOpenTab} />
                ))}
              </ul>
            </Fold>
          )}

          <NoPathwayRecord view={view} />
        </StepCard>
        )}

        <ReadinessStep view={view} pathwayId={showCurrent ? currentPathway?.id ?? null : picked?.id ?? consider[0]?.id ?? null} />
        <TwinStep view={view} />
        <MonitoringStep view={view} />
        {savedHere && picked && !showCurrent && (
          <HrtSendStep view={view} pathway={picked} pick={current} prices={prices} onOpenOpr={onOpenOpr} onPlanSent={onPlanSent} />
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * A patient already on hormones
 * ------------------------------------------------------------------ */

/**
 * Step 1 for a patient on hormones: the pathway in effect, what was chosen and why, since when, and how it reads
 * today (its band now, any lock or review a newer lab or check-in brought). Changing it opens the pathway list.
 */
function CurrentTherapy({
  view,
  pathway,
  onChange,
  onOpenTab,
}: {
  view: HrtPatientView;
  pathway: HrtPathway;
  onChange: () => void;
  onOpenTab: (tab: HrtTabKey) => void;
}) {
  const provider = useAuth().can('ADMIN', 'CLINICIAN');
  const latest = view.selections[0];
  if (!latest || latest.kind !== 'selected') return null;
  const schedule = pathway.controlled ? pathwaySchedule(view.products[pathway.id]) : null;
  const names = latest.products.map((p) => p.name);
  const next = view.status.nextMonitoring;
  const window = next ? dueWindow(next) : null;
  return (
    <StepCard
      step={1}
      title="Current Hormone Therapy"
      subtitle={`${view.patient.firstName} started hormones on ${shortDay(view.status.startedOn)}. MCOS keeps checking the pathway against every new lab and check-in.`}
      action={
        provider ? (
          <button
            type="button"
            onClick={onChange}
            className="inline-flex h-[30px] items-center rounded-md border border-hairline bg-white px-3 text-[12.5px] text-navy hover:bg-mint"
            data-testid="change-therapy"
          >
            Change pathway or dose
          </button>
        ) : undefined
      }
      testId="hrt-current-therapy"
    >
      <div className="rounded-[10px] border border-sage bg-white px-3 py-3 shadow-[0_0_0_1px_var(--sage-soft)]">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-display text-[18px] leading-tight text-navy" style={{ fontWeight: 600 }}>{pathway.name}</p>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
              {pathway.controlled && <HrtChip>{schedule ? `Controlled: ${schedule}` : 'Controlled'}</HrtChip>}
              <span className="inline-flex items-center gap-1 rounded-[5px] bg-mint px-[7px] py-[3px] text-[11.5px] font-semibold leading-none text-forest">
                <IconCheck size={12} />
                Started {shortDay(view.status.startedOn)}
              </span>
              {pathway.reviewRequired && <span className="rounded-[5px] bg-risk-yellow-soft px-[7px] py-[3px] text-[11.5px] font-semibold leading-none text-risk-yellow">Review</span>}
            </div>
            <p className="mt-2 text-[14px] leading-snug text-navy" data-testid="current-products">
              {names.length > 0 ? joinNames(names) : 'No product recorded'}
              {latest.doseMgWeek != null ? `, ${latest.doseMgWeek} mg/week` : ''}
            </p>
            <p className="mt-1 text-[12.5px] leading-snug text-ink-muted">
              Chosen {shortDay(latest.createdAt)}
              {latest.clinician ? ` by ${latest.clinician.name}` : ''}: {latest.reason}
            </p>
          </div>
          <span className="hidden shrink-0 sm:block">
            <BandBox band={pathway.band} />
          </span>
        </div>
        <div className="mt-2 sm:hidden">
          <BandBox band={pathway.band} compact />
        </div>
        {pathway.band === 'LOCKED' && (
          <p className="mt-2 rounded-md bg-alert-soft px-2.5 py-2 text-[12.5px] leading-snug text-alert" role="status" data-testid="current-locked">
            <b className="font-semibold">Locked now: </b>
            {pathway.locks.map((lock) => lock.text).join(' ')}
          </p>
        )}
        <FindingNotes pathway={pathway} />
        <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1.5 border-t border-hairline pt-2 text-[12.5px] text-ink-muted">
          {next && (
            <button type="button" onClick={() => onOpenTab('labs')} className="text-left hover:text-forest" data-testid="current-next-check">
              <span className="font-semibold text-navy">Next check: </span>
              {next.when.charAt(0).toUpperCase() + next.when.slice(1)}
              {window ? `, ${window}` : ''}
              {next.state === 'due_now' ? ' (due now)' : next.state === 'overdue' ? ' (overdue)' : ''}
            </button>
          )}
          {view.status.openAlerts > 0 && (
            <button type="button" onClick={() => onOpenTab('checkins')} className="font-semibold text-alert hover:underline" data-testid="current-alerts">
              {view.status.openAlerts} check-in {view.status.openAlerts === 1 ? 'alert' : 'alerts'} open
            </button>
          )}
        </div>
      </div>
    </StepCard>
  );
}

/* ------------------------------------------------------------------ *
 * A pathway card
 * ------------------------------------------------------------------ */

function PathwayCard({
  view,
  pathway,
  selected,
  onSelect,
  pick,
  setPick,
  prices,
  quiet = false,
}: {
  view: HrtPatientView;
  pathway: HrtPathway;
  selected: boolean;
  onSelect: () => void;
  pick: HrtPick;
  setPick: (next: HrtPick) => void;
  prices: Prices;
  quiet?: boolean;
}) {
  const [whyOpen, setWhyOpen] = useState(false);
  const products = view.products[pathway.id] ?? [];
  const schedule = pathway.controlled ? pathwaySchedule(products) : null;
  const latest = view.selections[0];
  const saved = latest?.kind === 'selected' && latest.pathwayId === pathway.id ? latest : null;

  return (
    <li
      className={`rounded-[10px] border bg-white transition-colors ${selected ? 'border-sage shadow-[0_0_0_1px_var(--sage-soft)]' : 'border-hairline hover:border-hairline'}`}
      data-testid={`pathway-${pathway.id}`}
    >
      {/* As drawn: the band box sits to the right of the name, the chips, the summary and the id; on a phone it goes under the name. */}
      <div className="flex items-start gap-3 px-3 pt-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <button type="button" role="radio" aria-checked={selected} onClick={onSelect} className="flex min-w-0 items-start gap-3 text-left">
              <span className="mt-0.5 self-start">
                <RadioMark checked={selected} />
              </span>
              <span className={`min-w-0 pt-[2px] font-display leading-tight text-navy ${quiet ? 'text-[16.5px]' : 'text-[18px]'}`} style={{ fontWeight: 600 }}>
                {pathway.name}
              </span>
            </button>
            {pathway.why && (
              <button
                type="button"
                aria-expanded={whyOpen}
                onClick={() => setWhyOpen((was) => !was)}
                className="text-[12.5px] font-semibold text-forest underline underline-offset-2 max-sm:pl-[38px]"
                data-testid={`why-${pathway.id}`}
              >
                {whyOpen ? 'Hide why' : 'Why?'}
              </button>
            )}
          </div>
          <div className="pl-[38px]">
            <div className="mt-2 sm:hidden">
              <BandBox band={pathway.band} compact />
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
              {pathway.controlled && <HrtChip>{schedule ? `Controlled: ${schedule}` : 'Controlled'}</HrtChip>}
              {saved && (
                <span className="inline-flex items-center gap-1 rounded-[5px] bg-mint px-[7px] py-[3px] text-[11.5px] font-semibold leading-none text-forest" data-testid="saved-chip">
                  <IconCheck size={12} />
                  Chosen {shortDay(saved.createdAt)}
                  {saved.clinician ? ` by ${saved.clinician.name}` : ''}
                </span>
              )}
              {pathway.reviewRequired && <span className="rounded-[5px] bg-risk-yellow-soft px-[7px] py-[3px] text-[11.5px] font-semibold leading-none text-risk-yellow">Review</span>}
              {pathway.caps.length > 0 && (
                <span className="inline-flex items-center gap-1.5 text-[11.5px] text-ink-muted">
                  Band capped
                  <InfoHint label={`Why ${pathway.name} is capped`}>
                    {pathway.caps.map((cap) => (
                      <span key={cap.id} className="mb-1 block last:mb-0">
                        {cap.text}
                        <span className="block opacity-75">{ruleSource(cap)}</span>
                      </span>
                    ))}
                  </InfoHint>
                </span>
              )}
            </div>
            {pathway.summary && <p className="mt-1.5 text-[13.5px] leading-snug text-ink-muted">{pathway.summary}</p>}
            <p className="mt-1 text-[12.5px] text-ink-muted">
              {pathway.id}
              {pathway.dominantPhenotype?.name ? ` · ${pathway.dominantPhenotype.name} phenotype` : ''}
            </p>
          </div>
        </div>
        <span className="hidden shrink-0 sm:block">
          <BandBox band={pathway.band} />
        </span>
      </div>

      <div className="px-3 pb-3 pl-[50px] max-sm:pl-3">
        {whyOpen && pathway.why && <WhyCard why={pathway.why} evidence={pathway.evidence} />}

        <FindingNotes pathway={pathway} />

        {selected && (
          <>
            <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.07em] text-ink-muted">
              Products from your pharmacy list · doses as the pharmacy lists them
            </p>
            {products.length === 0 ? (
              <p className="mt-1.5 rounded-lg border border-dashed border-hairline px-3 py-3 text-[13px] text-ink-muted">
                The dose library lists no product for this pathway.
              </p>
            ) : (
              <ul className="mt-1.5 space-y-2">
                {products.map((product) => (
                  <ProductRow
                    key={product.medicationId}
                    product={product}
                    item={product.formularyItemId ? prices.byItemId.get(product.formularyItemId) ?? null : null}
                    ticked={pick.products.includes(product.medicationId)}
                    loadingPrices={prices.isLoading}
                    onToggle={() =>
                      setPick({
                        ...pick,
                        products: pick.products.includes(product.medicationId)
                          ? pick.products.filter((m) => m !== product.medicationId)
                          : [...pick.products, product.medicationId],
                      })
                    }
                    prices={prices}
                  />
                ))}
              </ul>
            )}
            <ChoiceForm view={view} pathway={pathway} pick={pick} setPick={setPick} />
          </>
        )}
      </div>
    </li>
  );
}

/** Level 2 review and Level 3 caution findings, the engine's flags (progestogen, reason required) and caps. */
function FindingNotes({ pathway }: { pathway: HrtPathway }) {
  const flags = pathway.flags.filter((flag) => !/^Reason required when selected/.test(flag));
  if (pathway.review.length === 0 && pathway.caution.length === 0 && flags.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1">
      {pathway.review.map((finding) => (
        <li key={finding.id} className="flex gap-2 rounded-md bg-risk-yellow-soft px-2.5 py-1.5 text-[12.5px] leading-snug text-risk-yellow">
          <span className="shrink-0 font-semibold">Review</span>
          <span className="min-w-0 flex-1">
            {finding.text}
            {finding.referralText ? ` Referral: ${finding.referralText}.` : ''}
          </span>
          <SourceHint finding={finding} />
        </li>
      ))}
      {pathway.caution.map((finding) => (
        <li key={finding.id} className="flex gap-2 rounded-md bg-paper-deep px-2.5 py-1.5 text-[12.5px] leading-snug text-ink-muted">
          <span className="shrink-0 font-semibold">Caution</span>
          <span className="min-w-0 flex-1">{finding.text}</span>
          <SourceHint finding={finding} />
        </li>
      ))}
      {flags.map((flag) => (
        <li key={flag} className="flex gap-2 rounded-md bg-info-soft px-2.5 py-1.5 text-[12.5px] leading-snug text-navy">
          <span className="shrink-0 font-semibold">Note</span>
          <span>{flag}</span>
        </li>
      ))}
    </ul>
  );
}

/** The rule id and the spec's source for a finding, behind the hint instead of in the line. */
function SourceHint({ finding }: { finding: HrtFinding }) {
  return (
    <span className="shrink-0 pt-[1px]">
      <InfoHint label={`Source of ${finding.name}`}>{ruleSource(finding)}</InfoHint>
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * A product
 * ------------------------------------------------------------------ */

function ProductRow({
  product,
  item,
  ticked,
  loadingPrices,
  onToggle,
  prices,
}: {
  product: HrtProduct;
  item: OprItem | null;
  ticked: boolean;
  loadingPrices: boolean;
  onToggle: () => void;
  prices: Prices;
}) {
  const schedule = product.controlledSchedule ? pathwaySchedule([product]) : null;
  const note = productNote(product);
  const dose = item?.doseLine ?? libraryDoseLine(product);
  // Held back by the dose library until MCOS approves the exact product: it can be priced, not put on a plan yet.
  const held = product.heldBack ? 'Not on a plan until MCOS approves the exact product.' : null;
  return (
    <li
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-hairline bg-white px-2.5 py-2.5 sm:flex-nowrap"
      data-testid={`product-${product.medicationId}`}
    >
      <button type="button" aria-pressed={ticked} onClick={onToggle} className="flex min-w-0 basis-full items-center gap-3 text-left sm:flex-1 sm:basis-0">
        <TickBox checked={ticked} />
        <span className="min-w-0 flex-1">
          <span className="block text-[14.5px] font-semibold leading-snug text-navy">
            {product.name}
            {product.role === 'adjunct' ? ' (add-on)' : ''}
          </span>
          {schedule && (
            <span className="mt-1 block">
              <HrtChip>Controlled: {schedule}</HrtChip>
            </span>
          )}
          <span className="mt-1 block text-[13px] leading-snug text-ink-muted">{dose}</span>
          {note && <span className="mt-0.5 block text-[12px] leading-snug text-plum">{note}</span>}
          {held && <span className="mt-0.5 block text-[12px] font-medium leading-snug text-risk-yellow">{held}</span>}
        </span>
      </button>
      <div className="max-sm:pl-[38px]">
        {item ? (
          <PriceBox item={item} onSave={(change) => prices.save(item.itemId, change)} onDraft={(cents) => prices.setDraft(item.itemId, cents)} />
        ) : (
          <div className="flex w-[158px] flex-col gap-1" data-price-box="">
            <span className="flex h-11 items-center rounded-[9px] border border-dashed border-hairline bg-paper px-3 text-[12.5px] text-ink-muted">
              {loadingPrices ? 'Loading price' : 'Not in your price book'}
            </span>
          </div>
        )}
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ *
 * The dark box
 * ------------------------------------------------------------------ */

function SummaryBox({
  pathway,
  view,
  pick,
  prices,
  onOpenOpr,
}: {
  pathway: HrtPathway;
  view: HrtPatientView;
  pick: HrtPick;
  prices: Prices;
  onOpenOpr: () => void;
}) {
  const products = (view.products[pathway.id] ?? []).filter((p) => pick.products.includes(p.medicationId));
  const items = products.map((p) => (p.formularyItemId ? prices.byItemId.get(p.formularyItemId) : undefined)).filter((i): i is OprItem => Boolean(i));
  const unpriceable = products.length - items.length;
  const banner = pricingBanner(bannerLines(items));
  const missing = banner.missing + unpriceable;
  const allSet = products.length > 0 && missing === 0;
  const margin = banner.marginMonthlyCents;

  return (
    <div className="rounded-xl bg-navy px-[22px] py-[18px] text-white" data-testid="hrt-summary-box">
      <div className="flex flex-wrap gap-x-10 gap-y-3">
        <div className="min-w-0">
          <div className="text-[13px] text-white/80">Patient pays</div>
          <div className="font-display text-[28px] font-semibold leading-tight" data-testid="hrt-patient-pays">
            {usd(banner.monthlyCents)}/mo
          </div>
          {banner.perVisitCents > 0 && <div className="text-[13px] text-white/80">+ {usd(banner.perVisitCents)} per in-clinic visit</div>}
          <div className="text-[12.5px] text-white/80">
            {pathway.name} · {products.length === 1 ? '1 product' : `${products.length} products`}
          </div>
        </div>
        <div className="min-w-0 sm:pt-[18px]">
          <div className="text-[13px] text-white/80">{banner.marginEstimated ? 'Your margin (est.)' : 'Your margin'}</div>
          <div className="font-display text-[22px] font-semibold leading-tight">{margin != null ? `${wholeUsd(margin)}/mo` : 'No cost yet'}</div>
        </div>
      </div>
      <div className="mt-3.5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5 text-[14.5px]">
          <span className={`grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full ${allSet ? 'bg-sage' : 'bg-white/15'}`}>
            <IconCheck size={14} />
          </span>
          <span className="text-white/90" data-testid="hrt-price-status">
            {products.length === 0
              ? 'Tick a product above to price it.'
              : allSet
                ? banner.status(view.patient.firstName)
                : `${missing} ${missing === 1 ? 'price' : 'prices'} to set above.`}
          </span>
        </div>
        <button
          type="button"
          onClick={onOpenOpr}
          className="flex min-h-11 shrink-0 items-center rounded-[9px] border border-white/30 px-4 text-[15px] font-semibold text-white hover:bg-white/10"
        >
          Open OPR Pricing
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Saving the choice
 * ------------------------------------------------------------------ */

function useSelection(view: HrtPatientView, onDone?: () => void) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState<{ tone: 'ok' | 'warn' | 'error'; text: string } | null>(null);
  const mutation = useMutation({
    mutationFn: (body: { pathwayId: string | null; medicationId: string | null; products?: string[]; doseMgWeek: number | null; reason: string }) =>
      api.post<{ selection: { id: string; kind: string }; warnings: string[] }>(`/hrt/patients/${view.patient.id}/selection`, {
        ...body,
        evaluationId: view.evaluation?.id ?? null,
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: hrtKey(view.patient.id) });
      void queryClient.invalidateQueries({ queryKey: HRT_PROGRAM_KEY });
      void queryClient.invalidateQueries({ queryKey: ['treatment-queue'] });
      setNote(
        result.warnings.length > 0
          ? { tone: 'warn', text: `Saved. ${result.warnings.join(' ')}` }
          : { tone: 'ok', text: result.selection.kind === 'no_pathway' ? 'No pathway recorded, with your reason.' : 'Pathway saved, with your reason.' },
      );
      onDone?.();
    },
    onError: (caught) => {
      if (caught instanceof ApiClientError && caught.status === 409) {
        void queryClient.invalidateQueries({ queryKey: hrtKey(view.patient.id) });
        setNote({ tone: 'warn', text: 'The evaluation changed while you were choosing (a new lab, answer or confirmation). Review the updated pathways and save again.' });
        return;
      }
      setNote({ tone: 'error', text: errorText(caught, 'Could not save the choice.') });
    },
  });
  return { mutation, note, setNote };
}

function ChoiceForm({ view, pathway, pick, setPick }: { view: HrtPatientView; pathway: HrtPathway; pick: HrtPick; setPick: (next: HrtPick) => void }) {
  const provider = useAuth().can('ADMIN', 'CLINICIAN');
  const products = view.products[pathway.id] ?? [];
  // Every ticked product goes with the choice, the main product first (HRT-02: the estrogen, then the progestogen).
  const ticked = products.filter((p) => pick.products.includes(p.medicationId));
  const ordered = [...ticked.filter((p) => p.role === 'primary'), ...ticked.filter((p) => p.role !== 'primary')];
  const primary = ordered[0] ?? null;
  const doseAllowed = primary && primary.exceptionAboveMgWeek !== null && !primary.noAutomaticDose;
  const doseValue = doseAllowed && pick.dose.trim() ? Number(pick.dose) : null;
  const doseBad = doseValue !== null && (!Number.isFinite(doseValue) || doseValue <= 0);
  const latest = view.selections[0];
  const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((m) => b.includes(m));
  const savedSame =
    latest?.kind === 'selected' &&
    latest.pathwayId === pathway.id &&
    sameSet(latest.products.map((p) => p.medicationId), ordered.map((p) => p.medicationId)) &&
    (latest.doseMgWeek ?? null) === (doseBad ? null : doseValue);
  const [editing, setEditing] = useState(false);
  useEffect(() => setEditing(false), [pathway.id]);
  const { mutation, note, setNote } = useSelection(view, () => {
    setPick({ ...pick, reason: '' });
    setEditing(false);
  });
  useEffect(() => setNote(null), [pathway.id, setNote]);
  const reasonOk = pick.reason.trim().length >= 5;

  if (!provider) {
    return (
      <p className="mt-3 rounded-lg bg-paper-deep px-3.5 py-2.5 text-[13px] text-ink-muted">
        {savedSame && latest ? `Chosen by ${latest.clinician?.name ?? 'a clinician'}: ${latest.reason}` : 'A clinician records the pathway choice. You can tick products and set prices.'}
      </p>
    );
  }

  if (savedSame && latest && !editing) {
    return (
      <div className="mt-3 rounded-lg bg-mint px-3.5 py-2.5 text-[13px] text-navy" data-testid="saved-choice">
        <p>
          <b className="font-semibold text-forest">Saved</b>
          {latest.products.length > 0 ? ` with ${joinNames(latest.products.map((p) => p.name))}` : ''}
          {latest.doseMgWeek != null ? `, ${latest.doseMgWeek} mg/week` : ''}. Reason: {latest.reason}
        </p>
        {latest.warnings.length > 0 && <p className="mt-1 text-[12.5px] text-risk-yellow">{latest.warnings.join(' ')}</p>}
        <button type="button" onClick={() => setEditing(true)} className="mt-1 text-[12.5px] font-semibold text-forest underline underline-offset-2">
          Record a new reason
        </button>
        {note && <SaveNote tone={note.tone}>{note.text}</SaveNote>}
      </div>
    );
  }

  return (
    <form
      className="mt-3 rounded-lg border border-hairline bg-paper px-3 py-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!reasonOk || doseBad || mutation.isPending) return;
        mutation.mutate({
          pathwayId: pathway.id,
          medicationId: primary?.medicationId ?? null,
          products: ordered.map((p) => p.medicationId),
          doseMgWeek: doseBad ? null : doseValue,
          reason: pick.reason.trim(),
        });
      }}
      data-testid="choice-form"
    >
      <label className="block">
        <span className="block text-[12.5px] font-semibold text-navy">
          {pathway.requiresReasonOnSelect ? `This pathway needs a reason: ${pathway.requiresReasonOnSelect}` : `Your reason for choosing ${pathway.name.toLowerCase()}`}
        </span>
        <textarea
          value={pick.reason}
          onChange={(event) => setPick({ ...pick, reason: event.target.value })}
          rows={2}
          maxLength={2000}
          placeholder={`Why this pathway is right for ${view.patient.firstName}, in your words`}
          className={`${fieldClass} mt-1.5 min-h-[64px] resize-y`}
          data-testid="choice-reason"
        />
      </label>
      {doseAllowed && primary && (
        <label className="mt-2 flex flex-wrap items-center gap-2 text-[12.5px] text-navy">
          <span className="font-semibold">Dose for {primary.name}</span>
          <span className="inline-flex items-center gap-1.5">
            <input
              inputMode="decimal"
              value={pick.dose}
              onChange={(event) => setPick({ ...pick, dose: event.target.value.replace(/[^0-9.]/g, '') })}
              placeholder="optional"
              aria-label={`Dose for ${primary.name} in mg per week`}
              className={`${fieldClass} w-[96px] py-1.5`}
            />
            mg/week
          </span>
          {doseValue !== null && !doseBad && primary.exceptionAboveMgWeek !== null && doseValue > primary.exceptionAboveMgWeek && (
            <span className="basis-full text-risk-yellow">Above {primary.exceptionAboveMgWeek} mg/week is an exception review.</span>
          )}
        </label>
      )}
      <div className="mt-2.5 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={!reasonOk || doseBad || mutation.isPending}
          className="inline-flex h-[38px] items-center gap-2 rounded-md bg-forest px-4 text-[14px] font-semibold text-white hover:bg-forest-deep disabled:opacity-50"
          data-testid="save-choice"
        >
          {mutation.isPending ? 'Saving' : savedSame ? 'Save the new reason' : 'Save pathway choice'}
        </button>
        {editing && (
          <button type="button" onClick={() => setEditing(false)} className="text-[13px] text-ink-muted underline underline-offset-2">
            Cancel
          </button>
        )}
        <span className="text-[12px] text-ink-muted" data-testid="recorded-with">
          {ordered.length > 0 ? `Recorded with ${joinNames(ordered.map((p) => p.name))}.` : 'No product ticked: the pathway is recorded on its own.'}
        </span>
      </div>
      {note && <SaveNote tone={note.tone}>{note.text}</SaveNote>}
    </form>
  );
}

/** "None of these": the choice not to start a hormone pathway, stored with its reason like any other. */
function NoPathwayRecord({ view }: { view: HrtPatientView }) {
  const provider = useAuth().can('ADMIN', 'CLINICIAN');
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const { mutation, note } = useSelection(view, () => {
    setReason('');
    setOpen(false);
  });
  if (!provider) return null;
  return (
    <div className="mt-3 border-t border-hairline pt-2.5">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} className="text-[13px] font-semibold text-forest underline underline-offset-2" data-testid="no-pathway-open">
          None of these fits: record no pathway
        </button>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (reason.trim().length >= 5) mutation.mutate({ pathwayId: null, medicationId: null, doseMgWeek: null, reason: reason.trim() });
          }}
          className="rounded-lg border border-hairline bg-paper px-3 py-3"
        >
          <label className="block text-[12.5px] font-semibold text-navy">
            Why no hormone pathway for {view.patient.firstName}
            <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={2} maxLength={2000} className={`${fieldClass} mt-1.5`} data-testid="no-pathway-reason" />
          </label>
          <div className="mt-2 flex items-center gap-3">
            <button
              type="submit"
              disabled={reason.trim().length < 5 || mutation.isPending}
              className="inline-flex h-[36px] items-center rounded-md border border-forest bg-white px-4 text-[14px] font-semibold text-forest hover:bg-mint disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving' : 'Record no pathway'}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="text-[13px] text-ink-muted underline underline-offset-2">
              Cancel
            </button>
          </div>
        </form>
      )}
      {note && <SaveNote tone={note.tone}>{note.text}</SaveNote>}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * A locked pathway
 * ------------------------------------------------------------------ */

function LockedCard({ view, pathway, onOpenTab }: { view: HrtPatientView; pathway: HrtPathway; onOpenTab: (tab: HrtTabKey) => void }) {
  const [whyOpen, setWhyOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const labLock = pathway.locks.some((lock) => lock.id === 'LAB-MALE-T' || lock.id === 'LAB-FEMALE-T');
  const unlocks = useMemo(() => pathway.locks.map((lock) => ({ lock, how: unlockWords(lock, view.confirmations) })), [pathway.locks, view.confirmations]);
  /** Opens Labs & Monitoring at the card that unlocks this pathway. */
  const openAt = (card: 'confirmations' | 'hormone-labs') => {
    onOpenTab('labs');
    window.setTimeout(() => document.querySelector(`[data-testid="${card}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 320);
  };
  return (
    <li className="rounded-[10px] border border-hairline bg-paper px-3 py-3" data-testid={`locked-${pathway.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full bg-paper-deep text-ink-muted">
            <LockGlyph size={14} />
          </span>
          <div className="min-w-0">
            <p className="font-display text-[16.5px] leading-tight text-navy" style={{ fontWeight: 600 }}>{pathway.name}</p>
            <p className="mt-0.5 text-[12.5px] text-ink-muted">
              {pathway.id} · {BAND_WORD.LOCKED}
              {pathway.controlled ? ` · Controlled${pathwaySchedule(view.products[pathway.id]) ? `: ${pathwaySchedule(view.products[pathway.id])}` : ''}` : ''}
            </p>
          </div>
        </div>
        <span className="hidden sm:block"><BandBox band="LOCKED" compact /></span>
      </div>
      <ul className="mt-2 space-y-1.5 sm:pl-[38px]">
        {unlocks.map(({ lock, how }) => (
          <li key={lock.id} className="rounded-md bg-white px-2.5 py-2 text-[12.5px] leading-snug ring-1 ring-hairline" data-testid={`lock-${lock.id}`}>
            <p className="flex items-start gap-2 text-navy">
              <span className="min-w-0 flex-1">{lock.text}</span>
              <SourceHint finding={lock} />
            </p>
            {how && (
              <p className="mt-1 text-ink-muted">
                <span className="font-semibold text-forest">Unlocks when:</span> {how.text}.{' '}
                {how.key && (
                  <button type="button" onClick={() => openAt('confirmations')} className="font-semibold text-forest underline underline-offset-2">
                    Set it on Labs & Monitoring
                  </button>
                )}
              </p>
            )}
            {lock.referralText && <p className="mt-1 text-ink-muted">Referral: {lock.referralText}.</p>}
          </li>
        ))}
        {pathway.missing.length > 0 && (
          <li className="text-[12.5px] text-ink-muted">
            <span className="font-semibold">Still needed:</span> {pathway.missing.join(', ')}.{' '}
            {labLock && (
              <button type="button" onClick={() => openAt('hormone-labs')} className="font-semibold text-forest underline underline-offset-2">
                Add results
              </button>
            )}
          </li>
        )}
        {pathway.review.map((finding) => (
          <li key={finding.id} className="flex items-start gap-2 text-[12.5px] text-risk-yellow">
            <span className="min-w-0 flex-1">
              <span className="font-semibold">Review after unlock:</span> {finding.text}
            </span>
            <SourceHint finding={finding} />
          </li>
        ))}
      </ul>
      <div className="mt-2 flex flex-wrap gap-3 sm:pl-[38px]">
        {pathway.why && (
          <button type="button" aria-expanded={whyOpen} onClick={() => setWhyOpen((was) => !was)} className="text-[12.5px] font-semibold text-forest underline underline-offset-2">
            {whyOpen ? 'Hide why' : 'Why?'}
          </button>
        )}
        {pathway.nonHormonalOptions && (
          <button
            type="button"
            aria-expanded={optionsOpen}
            onClick={() => setOptionsOpen((was) => !was)}
            className="text-[12.5px] font-semibold text-forest underline underline-offset-2"
            data-testid={`nonhormonal-${pathway.id}`}
          >
            {optionsOpen ? 'Hide non-hormonal options' : 'Non-hormonal options, for reference'}
          </button>
        )}
      </div>
      {whyOpen && pathway.why && <div className="sm:pl-[38px]"><WhyCard why={pathway.why} evidence={pathway.evidence} /></div>}
      {optionsOpen && pathway.nonHormonalOptions && (
        <div className="sm:pl-[38px]">
          <WhyCard why={pathway.nonHormonalOptions.why} evidence={pathway.nonHormonalOptions.evidence} title="Non-hormonal options, for the clinician's reference" />
        </div>
      )}
    </li>
  );
}

export function HrtPlanLoading() {
  return (
    <section className="rounded-[10px] border border-hairline bg-white px-3 py-3">
      <LoadingRows rows={6} />
    </section>
  );
}
